import type { Component } from 'vue';
import { useDialog } from 'primevue/usedialog';

interface DynamicDialogInstance {
  close: (data?: unknown) => void;
}

export interface DialogOptions {
  header?: string;
  width?: string;
  modal?: boolean;
  closable?: boolean;
  /** Read inside the dialog component via PrimeVue's `useDialogRef()`. */
  data?: unknown;
  /** Forwarded as Vue props to the dialog component. */
  props?: Record<string, unknown>;
}

export interface DialogHandle<T = unknown> {
  close(result?: T): void;
  result: Promise<T | undefined>;
}

/**
 * Promise + handle wrapper around PrimeVue's `useDialog().open()`. Replaces
 * the legacy `modalFactory.showModal(controller, template)` API surface,
 * adds typed-result support, and provides `closeAll()` for the same
 * "drop everything on 401 / IdleTimeout" use case.
 */
export function useAppDialog(): {
  open<T = unknown>(component: Component, options?: DialogOptions): DialogHandle<T>;
  closeAll(): void;
} {
  const dialog = useDialog();
  const tracked = new Set<DynamicDialogInstance>();

  function open<T = unknown>(component: Component, options: DialogOptions = {}): DialogHandle<T> {
    let resolveResult!: (value: T | undefined) => void;
    const result = new Promise<T | undefined>((resolve) => {
      resolveResult = resolve;
    });

    const props: Record<string, unknown> = { ...(options.props ?? {}) };
    if (options.header !== undefined) props.header = options.header;
    props.modal = options.modal ?? true;
    props.closable = options.closable ?? true;
    if (options.width !== undefined) props.style = { width: options.width };

    // `emits` is spread as props onto the content component by DynamicDialog.
    // Combined with defineEmits(['close']) in the content, this gives a
    // reliable close path that doesn't depend on inject('dialogRef'). The
    // callbacks below reference `instance`, but only fire after it's assigned,
    // so a self-referential const is safe.
    const instance = dialog.open(component, {
      props,
      data: options.data,
      emits: {
        onClose: (data?: unknown) => instance.close(data),
      },
      onClose: (event?: { data?: unknown }) => {
        tracked.delete(instance);
        resolveResult(event?.data as T | undefined);
      },
    }) as DynamicDialogInstance;

    tracked.add(instance);

    return {
      close(value?: T) {
        instance.close(value);
      },
      result,
    };
  }

  function closeAll(): void {
    for (const instance of [...tracked]) {
      instance.close();
    }
    tracked.clear();
  }

  return { open, closeAll };
}
